import { useId, type CSSProperties } from "react"
import type { VehicleCard } from "../types/api"
import { useI18n } from "../hooks/useI18n"
import { localizeSignalTitle } from "../i18n/signalTitles"
import { localizeVehicleActivity } from "../i18n/vehicleActivity"

import flagWhite from "../assets/figma-main/flag-white.svg"
import flagBlue from "../assets/figma-main/flag-blue.svg"
import flagRed from "../assets/figma-main/flag-red.svg"
import searchAction from "../assets/figma-main/search-action.svg"
import eyeAndroid from "../assets/figma-main/eye-android.svg"
import eyeIOS from "../assets/figma-main/eye-ios.svg"
import notificationSuccess from "../assets/figma-main/notification-success.svg"
import notificationSuccessIos from "../assets/figma-main/notification-success-ios.svg"
import signalIconParkingAndroid from "../assets/figma-main/signal-icon-parking-android.svg"
import signalIconThanksAndroid from "../assets/figma-main/signal-icon-thanks-android.svg"
import signalIconParkingIOS from "../assets/figma-main/signal-icon-parking-ios.svg"
import signalIconThanksIOS from "../assets/figma-main/signal-icon-thanks-ios.svg"
import signalIconTow from "../assets/figma-main/signal-icon-tow.svg"
import { exactFigmaSignalColor, FigmaSignalGlyph, hasExactFigmaSignalGlyph } from "./FigmaSignalGlyphLibrary"
import { resolveStatusFrame } from "./StatusFrameResolver"
import "./FigmaSignalGlyphLibrary.css"

const ROAD_NUMBER_TRANSLITERATION: Record<string, string> = {
  А: "A", В: "B", Е: "E", К: "K", М: "M", Н: "H",
  О: "O", Р: "P", С: "C", Т: "T", У: "Y", Х: "X",
}

export const FIGMA_MAIN_REFERENCE: VehicleCard = {
  id: -1,
  plate_raw: "А172МУ888",
  plate_normalized: "А172МУ888",
  plate_display: "А 172 МУ 888",
  country_code: "RU",
  display_views: 987,
  real_search_count: 987,
  has_garage: true,
  garage_is_active: true,
  garage_owner_activity: { color: "yellow", label: "был недавно" },
  is_owner_viewing: false,
  current_status: null,
  signal_counters: [
    { signal_type_id: -101, slug: "nepravilno-priparkovalsya", title: "Неправильно припарковался", icon: "", color: "var(--bb-figma-signal-yellow-1)", category: "road", count: 8 },
    { signal_type_id: -102, slug: "spasibo", title: "Спасибо", icon: "", color: "var(--bb-figma-signal-green-1)", category: "attention", count: 3 },
    { signal_type_id: -103, slug: "mashinu-evakuiruyut", title: "Машину эвакуируют", icon: "", color: "var(--bb-figma-signal-red-3)", category: "urgent", count: 1 },
  ],
  card_share_count: 0,
}

export type MainVisualMode =
  | "android-recent"
  | "android-input-status"
  | "android-notification"
  | "ios-recent"
  | "ios-input-status"
  | "ios-notification"
  | "license-main-2digit"
  | "license-main-3digit"

export function mainVisualMode(): MainVisualMode | null {
  if (!import.meta.env.DEV) return null
  const value = new URLSearchParams(window.location.search).get("figma")
  const aliases: Record<string, MainVisualMode> = {
    main: "android-recent",
    status: "android-input-status",
    "main-android-recent": "android-recent",
    "main-android-input-status": "android-input-status",
    "main-android-notification": "android-notification",
    "main-ios-recent": "ios-recent",
    "main-ios-input-status": "ios-input-status",
    "main-ios-notification": "ios-notification",
    "license-main-2digit": "license-main-2digit",
    "license-main-3digit": "license-main-3digit",
  }
  return value ? aliases[value] || null : null
}

function vehicleForVisualCheck(vehicle: VehicleCard): VehicleCard {
  const mode = mainVisualMode()
  if (mode === "license-main-2digit") {
    return {
      ...FIGMA_MAIN_REFERENCE,
      plate_raw: "А172МУ77",
      plate_normalized: "А172МУ77",
      plate_display: "А 172 МУ 77",
    }
  }
  if (mode === "license-main-3digit") return FIGMA_MAIN_REFERENCE
  if (mode?.endsWith("recent")) return FIGMA_MAIN_REFERENCE
  if (mode?.endsWith("input-status") || mode?.endsWith("notification")) {
    return {
      ...FIGMA_MAIN_REFERENCE,
      garage_owner_activity: mode.endsWith("input-status")
        ? { color: "green", label: "онлайн" }
        : { color: "yellow", label: "был недавно" },
      current_status: {
        id: -1,
        slug: "special-series",
        title: "ОСОБАЯ СЕРИЯ",
        description: null,
        icon: "",
        color: "#6555D7",
      },
    }
  }
  return vehicle
}

function toRoadNumberGlyphs(value: string): string {
  return value.toUpperCase().split("").map((character) => ROAD_NUMBER_TRANSLITERATION[character] || character).join("")
}

export type PlateSegments = { prefix: string; digits: string; suffix: string; region: string }

export function splitPlate(value: Pick<VehicleCard, "plate_normalized" | "plate_display">): PlateSegments {
  const normalized = (value.plate_normalized || value.plate_display || "").replace(/\s+/g, "").toUpperCase()
  const match = normalized.match(/^([АВЕКМНОРСТУХA-Z])(\d{3})([АВЕКМНОРСТУХA-Z]{2})(\d{2,3})$/)
  if (match) {
    return {
      prefix: toRoadNumberGlyphs(match[1]),
      digits: match[2],
      suffix: toRoadNumberGlyphs(match[3]),
      region: match[4],
    }
  }
  return { prefix: "", digits: "", suffix: "", region: "" }
}

function maskStyle(src: string): CSSProperties {
  return { "--bbf-main-icon-mask": `url("${src}")` } as CSSProperties
}

export function formatCompactCount(value: number): string {
  const safeValue = Math.max(0, Math.floor(Number(value) || 0))
  if (safeValue >= 99_000) return "99K"
  if (safeValue < 1000) return String(safeValue)
  const shortValue = safeValue / 1000
  const rounded = shortValue >= 10 ? Math.floor(shortValue) : Math.floor(shortValue * 10) / 10
  return `${String(rounded).replace(".", ",")}K`
}

export function HeaderLogo() {
  return (
    <span className="bbf-logo-exact" role="img" aria-label="BeepApp">
      <span className="bbf-logo-beep">Beep</span><span className="bbf-logo-app">App</span>
    </span>
  )
}

export function RussianFlagCircle() {
  return (
    <span className="bbf-country-russia-exact" aria-hidden="true">
      <img src={flagWhite} alt="" draggable={false} />
      <img src={flagBlue} alt="" draggable={false} />
      <img src={flagRed} alt="" draggable={false} />
    </span>
  )
}

export function SearchIcon() {
  return <img className="bbf-search-action-exact" src={searchAction} alt="" draggable={false} />
}

export function FigmaLicensePlate({ plate, size = "main" }: { plate: Pick<VehicleCard, "plate_normalized" | "plate_display">; size?: "main" | "garage" }) {
  const { t } = useI18n()
  const { prefix, digits, suffix, region } = splitPlate(plate)
  return (
    <div className={`bbf-license-plate bbf-license-plate-${size}`} aria-label={`${t("main.plateAria")} ${plate.plate_display}`}>
      <i className="bbf-plate-outer-white" />
      <i className="bbf-plate-outer-dark" />
      <i className="bbf-plate-main-dark" />
      <i className="bbf-plate-region-dark" />
      <i className="bbf-plate-main-white" />
      <i className="bbf-plate-region-white" />
      <span className="bbf-plate-main-zone">
        <span className="bbf-plate-main-group">
          <span className="bbf-plate-prefix">{prefix}</span>
          <span className="bbf-plate-digits">{digits}</span>
          <span className="bbf-plate-suffix">{suffix}</span>
        </span>
      </span>
      <span className="bbf-plate-region-zone"><span className="bbf-plate-region">{region}</span></span>
      <span className="bbf-plate-rus">RUS</span>
      <span className="bbf-plate-flag" aria-hidden="true">
        <i className="white" /><i className="blue" /><i className="red" />
      </span>
    </div>
  )
}

export function FigmaStatusPlate({ vehicle }: { vehicle: VehicleCard }) {
  const { t } = useI18n()
  const statusFrame = resolveStatusFrame(vehicle.current_status)
  const statusGradientId = `bbf-main-status-${useId().replace(/:/g, "")}`

  return (
    <div className={`bbf-plate-wrap ${statusFrame ? "with-status" : ""}`} aria-label={`${t("main.plateAria")} ${vehicle.plate_display}`}>
      <FigmaLicensePlate plate={vehicle} />
      {statusFrame && (
        <div className="bbf-status-frame" aria-hidden="true">
          <svg className="bbf-status-frame-svg" viewBox="0 0 231 57">
            <defs>
              <linearGradient id={statusGradientId} x1="0" y1="28.5" x2="231" y2="28.5" gradientUnits="userSpaceOnUse">
                {statusFrame.stops.map((stop) => <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />)}
              </linearGradient>
            </defs>
            <path d="M227.002 0C228.719 2.08967e-05 230.111 1.39211 230.111 3.10938V53.4141C230.111 55.1314 228.719 56.5234 227.002 56.5234H3.10938C1.3921 56.5233 0 55.1314 0 53.4141V3.10938C0.000130048 1.39217 1.39217 0.000129849 3.10938 0H227.002ZM3.90918 3.10938C3.4676 3.1094 3.1094 3.4676 3.10938 3.90918V44.9561C3.10938 45.3977 3.46759 45.7558 3.90918 45.7559H226.202C226.644 45.7559 227.002 45.3977 227.002 44.9561V3.90918C227.002 3.46759 226.644 3.10938 226.202 3.10938H3.90918Z" fill={`url(#${statusGradientId})`} />
          </svg>
          <span style={{ left: "50%", width: "231px", transform: "translateX(-50%)", display: "flex", alignItems: "center", justifyContent: "center" }}>{statusFrame.label}</span>
        </div>
      )}
    </div>
  )
}

type SignalAssets = {
  tone: "parking" | "thanks" | "tow"
  iconAndroid: string
  iconIOS: string
}

function exactSignalAssets(signal: { slug?: string; title: string; category: "urgent" | "road" | "attention"; color: string }): SignalAssets | null {
  const slug = (signal.slug || "").trim().toLowerCase()
  if (slug === "bad_parking" || slug === "nepravilno-priparkovalsya") {
    return { tone: "parking", iconAndroid: signalIconParkingAndroid, iconIOS: signalIconParkingIOS }
  }
  if (slug === "thanks" || slug === "spasibo") {
    return { tone: "thanks", iconAndroid: signalIconThanksAndroid, iconIOS: signalIconThanksIOS }
  }
  if (slug === "car_being_towed" || slug === "mashinu-evakuiruyut") {
    return { tone: "tow", iconAndroid: signalIconTow, iconIOS: signalIconTow }
  }
  return null
}

function SignalCapsules({ vehicle }: { vehicle: VehicleCard }) {
  const { locale, direction, t } = useI18n()
  if (!vehicle.signal_counters.length) return null
  return (
    <div className="bbf-signal-capsules" aria-label={t("signalSelection.chooseSignal")}>
      {vehicle.signal_counters.map((counter) => {
        const assets = exactSignalAssets(counter)
        const figmaColor = exactFigmaSignalColor(counter.category, counter.slug)
        const hasMasterGlyph = hasExactFigmaSignalGlyph(counter.category, counter.slug) && Boolean(figmaColor)
        const hasExactIcon = Boolean(assets || hasMasterGlyph)
        const localizedTitle = localizeSignalTitle(locale, counter.title)
        if (!hasExactIcon && import.meta.env.DEV) console.warn(`[Figma exact] Missing Design Package visual for signal: ${counter.category}/${counter.slug || counter.title}`)
        return (
          <div
            className={`bbf-signal-capsule ${hasExactIcon ? "has-exact-icon" : "missing-exact-icon"}`}
            key={`${counter.signal_type_id}-${counter.is_starter ? "starter" : "real"}`}
            style={figmaColor ? ({ "--signal-color": figmaColor } as CSSProperties) : undefined}
            title={localizedTitle}
          >
            <span className="bbf-signal-counter-pill"><span className="bbf-signal-count">{counter.count}</span></span>
            {hasExactIcon && (
              <span className="bbf-signal-icon-exact" aria-hidden="true">
                {assets ? <i className={`circle tone-${assets.tone}`} /> : <i className="bbf-signal-circle-master" />}
                {assets ? (
                  <>
                    <span className="glyph android" style={maskStyle(assets.iconAndroid)} />
                    <span className="glyph ios" style={maskStyle(assets.iconIOS)} />
                  </>
                ) : <FigmaSignalGlyph category={counter.category} slug={counter.slug} />}
              </span>
            )}
            <span className="bbf-signal-label" dir={direction}>{localizedTitle}</span>
          </div>
        )
      })}
    </div>
  )
}

export function VehicleCardBlock({ vehicle }: { vehicle: VehicleCard }) {
  const { locale, direction, t } = useI18n()
  const displayVehicle = vehicleForVisualCheck(vehicle)
  const activity = displayVehicle.garage_owner_activity
  const activityLabel = localizeVehicleActivity(locale, activity?.label)
  return (
    <section className="bbf-vehicle-card" aria-label={`${t("main.shareCardPrefix")} ${displayVehicle.plate_display}`}>
      <div className="bbf-views">
        <span className="bbf-views-icon android" style={maskStyle(eyeAndroid)} aria-hidden="true" />
        <span className="bbf-views-icon ios" style={maskStyle(eyeIOS)} aria-hidden="true" />
        <span>{formatCompactCount(displayVehicle.display_views)}</span>
      </div>
      {activity && activityLabel && <div className={`bbf-owner-status ${activity.color}`} dir={direction}>{activityLabel}</div>}
      <FigmaStatusPlate vehicle={displayVehicle} />
      <SignalCapsules vehicle={displayVehicle} />
    </section>
  )
}

export function TopNotification({ text }: { text: string }) {
  return (
    <div className="bbf-top-notification" role="status">
      <div className="bbf-top-notification-content">
        <img className="android" src={notificationSuccess} alt="" draggable={false} />
        <img className="ios" src={notificationSuccessIos} alt="" draggable={false} />
        <span>{text}</span>
      </div>
    </div>
  )
}
